<template>
	<CardFooter
		:class="
			cn(propertyCardFooterBodyVariants({ variant, size }), props.class, '')
		"
	>
		<div class="flex max-h-fit w-full flex-col">
			<div class="mb-2 flex w-full justify-between">
				<span
					v-if="property.askingPrice"
					:class="propertyCardFooterPriceVariants({ size, variant })"
				>
					{{
						floatToLocaleString(property.askingPrice, {
							style: 'currency',
							minimumFractionDigits: 0,
							maximumFractionDigits: 0,
							roundingMode: 'ceil'
						})
					}}
				</span>
				<span
					v-if="property.commercialId"
					:class="cn(propertyCardCommercialIdVariants({ variant }), '')"
				>
					{{ property.commercialId }}
				</span>
			</div>

			<div class="flex w-full flex-col">
				<div
					:class="
						cn(
							propertyCardFooterTextsVariants({ size, variant }),
							'mb-2 flex flex-wrap items-center gap-2 leading-[18px]'
						)
					"
				>
					<PropertyCardItemSeparator
						v-if="!!property.area && shouldDisplayAreaLabel"
					>
						{{ property.area }}m²
						<span class="lowercase">
							{{ getAreaLabel(property.propertyType.identifier) }}
						</span>
					</PropertyCardItemSeparator>
					<PropertyCardItemSeparator
						v-if="!!property.totalArea && shouldDisplayTotalAreaLabel"
					>
						{{ property.totalArea }}m²
						<span class="lowercase">
							{{ getAreaLabel(property.propertyType.identifier) }}
						</span>
					</PropertyCardItemSeparator>

					<PropertyCardItemSeparator v-if="!!property.suites">
						{{ property.suites }}
						{{ grammarInflection(property.suites, 'suíte', 'suítes') }}
					</PropertyCardItemSeparator>
					<PropertyCardItemSeparator
						v-if="!property.suites && !!property.bedrooms"
					>
						{{ property.bedrooms }}
						{{ grammarInflection(property.bedrooms, 'quarto', 'quartos') }}
					</PropertyCardItemSeparator>
					<PropertyCardItemSeparator v-if="!!property.parkingSpots">
						{{ property.parkingSpots }}
						{{ grammarInflection(property.parkingSpots, 'vaga', 'vagas') }}
					</PropertyCardItemSeparator>
				</div>
				<div :class="propertyCardFooterAddressVariants({ size, variant })">
					{{ property.propertyType?.name }} em {{ property.region }},
					{{ property.city }} -
					{{ property.state }}
				</div>
			</div>
		</div>
	</CardFooter>
</template>
<script setup lang="ts">
import type { HTMLAttributes } from 'vue';
import type { Property } from '~/core/entities/property';

import { cn, floatToLocaleString } from '@/utils';

import {
	PropertyTypeEnum,
	propertyTypesTranslations
} from '~/core/entities/CRMEnums';
import {
	propertyCardCommercialIdVariants,
	propertyCardFooterAddressVariants,
	propertyCardFooterBodyVariants,
	propertyCardFooterPriceVariants,
	propertyCardFooterTextsVariants,
	PropertyCardItemSeparator
} from '.';
import { CardFooter } from '../ui/card';
import { grammarInflection } from '~~/utils';
import {
	getAreaLabel,
	propertyCountableExistsOnPropertyType
} from '~/core/entities/SystemEnums';

export type PropertyCardFooterProps = {
	class?: HTMLAttributes['class'];
	size: 'sm' | 'md' | 'lg';
	variant: 'whiteBackground' | 'transparentBackground' | 'fullFace';
	property: Property;
};

type PropertyCardCharacteristics = Omit<
	PropertyCardFooterProps['property'],
	'class' | 'variant' | 'size'
>;

const props = withDefaults(defineProps<PropertyCardFooterProps>(), {
	variant: 'whiteBackground',
	size: 'sm'
});

const shouldDisplayAreaLabel = computed(() => {
	return propertyCountableExistsOnPropertyType(
		props.property.propertyType.identifier,
		'area'
	);
});
const shouldDisplayTotalAreaLabel = computed(() => {
	return (
		!props.property.area &&
		propertyCountableExistsOnPropertyType(
			props.property.propertyType.identifier,
			'totalArea'
		)
	);
});
</script>
